/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { AfterViewInit, Component, ElementRef, Input, ViewChild } from '@angular/core';
import { BaseComponent } from '../base.component';
import { ErrorResponseModel } from '../../models';
import { LogService, NotifyService, UtilsService } from '../../services';
import { TwoFactorAuthApiService } from '../../http';
import { takeUntil } from 'rxjs/operators';

@Component({
    selector: 'app-two-factor-auth-app',
    templateUrl: './two-factor-auth-app.component.html',
    styleUrls: ['./two-factor-auth-app.component.scss']
})
export class TwoFactorAuthAppComponent extends BaseComponent implements AfterViewInit {
    /**
     * Holder to inject the QR code into the DOM.
     */
    @ViewChild('qrHolder') qrHolder: ElementRef;

    /**
     * Used to make API call to get QR code.
     */
    @Input() public userId: string;

    /**
     * Store the auth code to set up 2FA.
     */
    public authCode: string;

    /**
     * Toggle shimmer effect on token container.
     */
    public shimmer: boolean = false;

    constructor(
        private logService: LogService,
        private notifyService: NotifyService,
        private twoFactorAuthApiService: TwoFactorAuthApiService,
        private utilsService: UtilsService
    ) {
        super();
    }

    ngAfterViewInit(): void {
        this.getQrImageFromApi();
    }

    /**
     * Notify user the auth code has been copied to clipboard.
     */
    public authCodeCopied(): void {
        this.notifyService.success('Copied');

        this.shimmer = true;
        const timerId = setTimeout(() => {
            this.shimmer = false;
            clearTimeout(timerId);
        }, 750);
    }

    /**
     * Get Image from API.
     * @private
     */
    private getQrImageFromApi(): void {
        this.notifyService.muteLocalNotifications();
        this.twoFactorAuthApiService.setUp(this.userId)
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (data: any): void => {
                    this.authCode = data.code;
                    const decodedBlob = this.utilsService.base64toBlob(data.qrcode, 'image/png');
                    this.addImageToPage(decodedBlob);
                    this.notifyService.unmuteLocalNotifications();
                },
                error: (error: ErrorResponseModel): void => {
                    this.logService.error(
                        'AccountsTwoFactorAuthComponent:setupTwoFactorAuth',
                        'Error setting up Two Factor Auth',
                        error);
                }
            });
    }

    /**
     * Add blob
     * @param decodedBlob
     * @private
     */
    private addImageToPage(decodedBlob: Blob): void {
        const element = this.qrHolder.nativeElement;
        const img = document.createElement('img');
        img.id = 'qr-code';
        img.style.width = '160px';
        img.style.position = 'absolute';
        img.style.zIndex = '1';
        img.src = URL.createObjectURL(decodedBlob);
        element.appendChild(img);
    }
}
